'use client';

import { useEffect } from 'react';
import { VideoBase, VideoContent, VideoDescription, VideoPlayer, VideoTitle } from '@/app_components/Video/Video';
import { useOverrideStnPlayerStyles } from '@/app_lib/hooks/useOverrideStnPlayerStyles';
import type { AdTags } from '@/app_lib/services/ads/defaults';
import type { Video } from '@/app_lib/types/Video';

interface VideoHeroProps {
  video: Video;
  adTags?: AdTags[];
}

export const VideoHero = ({ video, adTags }: VideoHeroProps) => {
  useOverrideStnPlayerStyles('video-player-hero');

  useEffect(() => {
    const handlePlayerLoaded = () => {
      const vplayers = window.voltax?.getAllPlayersOnPage();
      if (!vplayers || vplayers.length === 0) {
        return;
      }

      const vplayer = vplayers[0];
      let isResetting = false;
      let hasEventListener = false;

      const setupTimeUpdate = () => {
        const container = document.querySelector('[class*="video-player-hero"]');
        const voltaxPlayer = container?.querySelector('voltax-player');
        const videoEl = voltaxPlayer?.shadowRoot?.querySelector('video');

        if (!videoEl || hasEventListener) {
          return;
        }

        videoEl.addEventListener('timeupdate', () => {
          // Loop the video before it finishes
          const secondsBeforeVideoFinishes = 0.7;
          if (!isResetting && videoEl.duration - videoEl.currentTime <= secondsBeforeVideoFinishes) {
            isResetting = true;
            vplayer.replacePlayerContent({ playerKey: video.id });
          }
        });

        hasEventListener = true;
      };

      vplayer.on('videoStart', () => {
        isResetting = false;
        setupTimeUpdate();

        // Interact with the video to avoid stay/next prompt
        const container = document.querySelector('[class*="video-player-hero"]');
        const shadowRoot = container?.querySelector('voltax-player')?.shadowRoot;
        const playBtn = shadowRoot?.querySelector<HTMLButtonElement>('.voltax-mp-play-button');
        playBtn?.click();
        playBtn?.click();
      });
    };

    if (window.voltax) {
      handlePlayerLoaded();
    } else {
      window.addEventListener('voltaxPlayerLoaded', handlePlayerLoaded);
    }

    return () => {
      window.removeEventListener('voltaxPlayerLoaded', handlePlayerLoaded);
    };
  }, [video.id]);

  return (
    <VideoBase
      dataCy='video-hero'
      layout='hero'
      orientation='vertical'
    >
      <VideoPlayer
        adTags={adTags}
        dataCy='video-hero-player'
        layout='hero'
        orientation='vertical'
        target='video-player-hero'
        videoId={video.id}
      />
      {video.showVideoDetails && (
        <VideoContent
          layout='hero'
          orientation='vertical'
        >
          <VideoTitle
            layout='hero'
            orientation='vertical'
            title={video.title}
          />
          <VideoDescription
            description={video.summary}
            layout='hero'
            orientation='vertical'
          />
        </VideoContent>
      )}
    </VideoBase>
  );
};
